import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchSocialStats, fetchSocialPosts } from '../../services/socialMediaService';
import { StatCard, Modal, Avatar } from '../../../../components/common';
import { SocialPost } from '../../components/SocialPost';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import {
    Loader2,
    TrendingUp,
    RefreshCcw,
    BarChart2,
    Camera,
    Layout,
    Users,
    ExternalLink,
    Heart,
    MessageSquare,
    User,
    Info
} from 'lucide-react';
import toast from 'react-hot-toast';

export const SocialMediaDashboard = () => {
    const [stats, setStats] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [error, setError] = useState(null);

    // Modal State lifted from SocialPost to prevent flickering/nesting
    const [selectedPost, setSelectedPost] = useState(null);

    // Engagement and comments tracking states
    const [engagementFilter, setEngagementFilter] = useState('Most Liked');
    const [expandedComments, setExpandedComments] = useState({});

    const loadData = useCallback(async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        else setRefreshing(true);
        setError(null);

        try {
            const [statsResponse, postsResponse] = await Promise.all([
                fetchSocialStats(),
                fetchSocialPosts()
            ]);

            setStats(statsResponse?.data?.data || null);
            setPosts(postsResponse?.data?.data || []);
        } catch (err) {
            console.error('[SOCIAL_DASHBOARD_LOAD_ERROR]', err);
            setError(err.message || 'Failed to load social media data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const totalEngagement = useMemo(() => {
        return posts.reduce((sum, post) => sum + (post.likes || 0) + (post.comments_count || 0), 0);
    }, [posts]);

    const filteredPosts = useMemo(() => {
        if (activeTab === 'all') return posts;
        return posts.filter(p => p.platform === activeTab);
    }, [posts, activeTab]);

    const topPosts = useMemo(() => {
        let sorted = [...posts];
        if (engagementFilter === 'Most Liked') {
            sorted.sort((a, b) => (b.likes || 0) - (a.likes || 0));
        } else if (engagementFilter === 'Most Commented') {
            sorted.sort((a, b) => (b.comments_count || 0) - (a.comments_count || 0));
        } else if (engagementFilter === 'Trending') {
            sorted.sort((a, b) => ((b.likes || 0) + (b.comments_count || 0)) - ((a.likes || 0) + (a.comments_count || 0)));
        }
        return sorted.slice(0, 5);
    }, [posts, engagementFilter]);

    const toggleComments = (postId) => {
        setExpandedComments(prev => ({
            ...prev,
            [postId]: !prev[postId]
        }));
    };

    const handleSync = async () => {
        await loadData(true);
        if (!error) toast.success("Dashboard Refreshed");
    };

    if (loading) {
        return (
            <div className="min-h-[600px] flex flex-col items-center justify-center space-y-4 text-center p-8 bg-slate-50/30 rounded-3xl border border-dashed border-slate-200">
                <Loader2 size={48} className="text-brand-blue animate-spin" />
                <p className="text-sm font-bold text-slate-400 animate-pulse uppercase tracking-widest">In Sync with Meta API...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-20">
            {/* Header Area */}
            {/* <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-black text-brand-dark tracking-tight">Social Media Dashboard</h1>
                  <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Role Analytics & Engagement Tracking</p>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={handleSync}
                        disabled={refreshing}
                        className="btn-secondary flex items-center gap-2 group"
                    >
                        <RefreshCcw size={14} className={refreshing ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'} />
                        {refreshing ? 'Refreshing...' : 'Refresh Data'}
                    </button>
                </div>
            </div> */}

            {/* Metrics Section - EXACTLY matching LeadsPage Style */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
                <StatCard
                    title="IG Followers"
                    value={stats?.instagram?.followers || 0}
                    icon={<Users />}
                    color="bg-brand-blue"
                    isExpanded={false}
                />
                <StatCard
                    title="FB Page Likes"
                    value={stats?.facebook?.likes || 0}
                    icon={<FaFacebook />}
                    iconBg="bg-blue-50 text-[#1877F2]"
                    isExpanded={false}
                />
                <StatCard
                    title="Content Library"
                    value={posts.length || stats?.instagram?.posts_count || 0}
                    icon={<FaInstagram />}
                    iconBg="bg-gradient-to-br from-purple-50 to-pink-50 text-pink-600"
                    isExpanded={false}
                />
                <StatCard
                    title="Active Engagement"
                    value={totalEngagement}
                    icon={<BarChart2 />}
                    iconBg="bg-amber-50 text-amber-600"
                    isExpanded={false}
                />
            </div>

            {/* Platform Tabs - Matching LeadsPage/ControlPanel style */}
            <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto no-scrollbar bg-white px-2 rounded-t-xl sticky top-0 z-20">
                <button
                    onClick={() => setActiveTab('all')}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-bold transition-all relative whitespace-nowrap ${activeTab === 'all'
                            ? "text-brand-blue border-b-2 border-brand-blue bg-blue-50/20"
                            : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                        }`}
                >
                    <Layout size={16} />
                    All Platforms
                </button>
                <button
                    onClick={() => setActiveTab('facebook')}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-bold transition-all relative whitespace-nowrap ${activeTab === 'facebook'
                            ? "text-brand-blue border-b-2 border-brand-blue bg-blue-50/20"
                            : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                        }`}
                >
                    <FaFacebook size={16} />
                    Facebook
                </button>
                <button
                    onClick={() => setActiveTab('instagram')}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-bold transition-all relative whitespace-nowrap ${activeTab === 'instagram'
                            ? "text-brand-blue border-b-2 border-brand-blue bg-blue-50/20"
                            : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                        }`}
                >
                    <FaInstagram size={16} />
                    Instagram
                </button>
            </div>

            {/* Posts Grid Section / Latest Engagements Section */}
            {activeTab === 'all' ? (
                <div className="space-y-6">
                    {/* Header and Filter bar */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                        <div>
                            <h2 className="text-lg font-black text-brand-dark tracking-tight">Latest Engagements</h2>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-0.5">Top 5 Trending & High‑Engagement Posts</p>
                        </div>
                        <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-200 w-fit self-start sm:self-auto shrink-0">
                            {['Most Liked', 'Most Commented', 'Trending'].map((filterOption) => (
                                <button
                                    key={filterOption}
                                    onClick={() => setEngagementFilter(filterOption)}
                                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                                        engagementFilter === filterOption
                                            ? 'bg-white text-brand-blue shadow-sm border border-slate-200/50'
                                            : 'text-slate-500 hover:text-slate-800'
                                    }`}
                                >
                                    {filterOption}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Top 5 list */}
                    <div className="space-y-4">
                        {topPosts.map((post, idx) => {
                            const isInstagram = post.platform === 'instagram';
                            const isExpanded = !!expandedComments[post.id];
                            
                            return (
                                <div key={post.id} className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden flex flex-col gap-4">
                                    {/* Platform Indicator Accent Line */}
                                    <div className={`absolute top-0 left-0 right-0 h-1 ${isInstagram ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500' : 'bg-blue-600'}`}></div>

                                    {/* Flex layout for row */}
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                        
                                        {/* Left Side: Thumbnail + Platform badge + Info */}
                                        <div className="flex items-center gap-4 flex-1 min-w-0">
                                            {post.media_url ? (
                                                <div className="relative w-16 h-16 rounded-2xl overflow-hidden shrink-0 border border-slate-100 bg-slate-50 shadow-inner">
                                                    <img src={post.media_url} alt="Post preview" className="w-full h-full object-cover" />
                                                    <div className={`absolute bottom-1 right-1 p-1 rounded-lg text-white shadow-md text-[10px] ${isInstagram ? 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500' : 'bg-[#1877F2]'}`}>
                                                        {isInstagram ? <FaInstagram size={10} /> : <FaFacebook size={10} />}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-md ${isInstagram ? 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500' : 'bg-[#1877F2]'}`}>
                                                    {isInstagram ? <FaInstagram size={22} /> : <FaFacebook size={22} />}
                                                </div>
                                            )}

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                        Rank #{idx + 1}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 font-bold">•</span>
                                                    <span className="text-[10px] text-slate-400 font-bold uppercase">
                                                        {new Date(post.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </span>
                                                </div>
                                                <p className="text-xs font-semibold text-slate-700 line-clamp-2 leading-relaxed">
                                                    {post.caption || "No caption provided"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Right Side: Stats + Expand Toggle button */}
                                        <div className="flex items-center gap-6 justify-between md:justify-end shrink-0 pt-4 md:pt-0 border-t md:border-t-0 border-slate-100">
                                            {/* Metrics */}
                                            <div className="flex items-center gap-6">
                                                <div className="flex flex-col items-center">
                                                    <div className="flex items-center gap-1 text-brand-orange">
                                                        <Heart size={14} className="fill-brand-orange" />
                                                        <span className="text-xs font-black text-slate-800">{(post.likes || 0).toLocaleString()}</span>
                                                    </div>
                                                    <span className="text-[9px] uppercase font-bold text-slate-400 tracking-wider mt-0.5">Likes</span>
                                                </div>
                                                <div className="flex flex-col items-center">
                                                    <div className="flex items-center gap-1 text-brand-blue">
                                                        <MessageSquare size={14} className="fill-brand-blue" />
                                                        <span className="text-xs font-black text-slate-800">{(post.comments_count || 0).toLocaleString()}</span>
                                                    </div>
                                                    <span className="text-[9px] uppercase font-bold text-slate-400 tracking-wider mt-0.5">Comments</span>
                                                </div>
                                            </div>

                                            {/* Expand Button */}
                                            <button
                                                onClick={() => toggleComments(post.id)}
                                                className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${
                                                    isExpanded 
                                                        ? 'bg-slate-800 border-slate-800 text-white shadow-md' 
                                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                }`}
                                            >
                                                {isExpanded ? 'Hide Comments' : 'View Comments'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Comments Scrollable Panel */}
                                    {isExpanded && (
                                        <div className="mt-2 border-t border-slate-100 pt-4 animate-in fade-in slide-in-from-top-4 duration-300">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Public Comments</h4>
                                                <span className="text-[9px] font-bold text-brand-blue bg-blue-50 px-2 py-0.5 rounded-full uppercase italic">
                                                    {post.comments_count || 0} Recorded
                                                </span>
                                            </div>
                                            <div className="max-h-48 overflow-y-auto space-y-2 pr-2 custom-scrollbar bg-slate-50/50 p-3 rounded-2xl border border-slate-100">
                                                {post.comments && post.comments.length > 0 ? (
                                                    post.comments.map((comment, cIdx) => (
                                                        <div key={cIdx} className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm flex flex-col gap-1">
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-[10px] font-black text-brand-blue uppercase">@{comment.name}</span>
                                                                <span className="text-[9px] text-slate-400 font-bold">
                                                                    {new Date(comment.timestamp || post.timestamp).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-slate-600 leading-relaxed font-semibold">{comment.message}</p>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="py-4 text-center text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                                                        No comments to display
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                /* Original Platform Posts Grid Section */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                    {filteredPosts.map(post => (
                        <SocialPost
                            key={post.id}
                            post={post}
                            onOpen={() => setSelectedPost(post)}
                        />
                    ))}
                    {filteredPosts.length === 0 && (
                        <div className="col-span-full py-20 text-center bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                            <p className="text-slate-400 font-bold uppercase tracking-[0.2em]">No posts found for this platform</p>
                        </div>
                    )}
                </div>
            )}

            {/* FULLSCREEN-STYLE LARGE MODAL - Rendered at Dashboard level to fix flickering */}
            {selectedPost && (
                <Modal
                    isOpen={!!selectedPost}
                    onClose={() => setSelectedPost(null)}
                    title={
                        <div className="flex items-center gap-3">
                            <div className={`p-2.5 rounded-xl text-white shadow-md ${selectedPost.platform === 'instagram' ? 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500' : 'bg-[#1877F2]'}`}>
                                {selectedPost.platform === 'instagram' ? <FaInstagram size={18} /> : <FaFacebook size={18} />}
                            </div>
                            <div>
                                <div className="text-sm font-black uppercase tracking-widest text-brand-dark">Conversation Hub</div>
                                <div className="text-[10px] text-slate-400 font-bold uppercase">{new Date(selectedPost.timestamp).toLocaleDateString()}</div>
                            </div>
                        </div>
                    }
                    size="max-w-4xl" // LARGE SIZE AS REQUESTED
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-h-[400px]">
                        {/* Left Side: Post Summary */}
                        <div className="space-y-6">
                            {selectedPost.media_url && (
                                <div className="rounded-3xl overflow-hidden shadow-2xl border border-slate-100 bg-slate-50 aspect-square lg:aspect-auto">
                                    <img src={selectedPost.media_url} alt="Original" className="w-full h-full object-cover max-h-[450px]" />
                                </div>
                            )}
                            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="p-2 bg-white rounded-xl shadow-sm text-brand-blue"><Info size={16} /></div>
                                    <span className="text-[11px] font-black uppercase text-slate-400 tracking-widest">Original Caption</span>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                                    {selectedPost.caption || "No caption provided for this post."}
                                </p>
                                <div className="flex items-center gap-6 mt-6 pt-6 border-t border-slate-200">
                                    <div className="flex items-center gap-2">
                                        <Heart size={18} className="text-brand-orange fill-brand-orange" />
                                        <span className="text-sm font-black text-brand-dark italic">{selectedPost.likes} Likes</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <MessageSquare size={18} className="text-brand-blue fill-brand-blue" />
                                        <span className="text-sm font-black text-brand-dark italic">{selectedPost.comments_count} Comments</span>
                                    </div>
                                </div>
                            </div>
                            <a
                                href={selectedPost.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary w-full flex items-center justify-center gap-2 py-4 rounded-2xl shadow-xl shadow-blue-100"
                            >
                                Open Original Posting <ExternalLink size={14} />
                            </a>
                        </div>

                        {/* Right Side: Comments List */}
                        <div className="flex flex-col h-full max-h-[600px] lg:max-h-[700px]">
                            <div className="mb-4 flex items-center justify-between px-1">
                                <span className="text-xs font-black uppercase tracking-widest text-slate-400">Public Discussion</span>
                                <span className="text-[10px] font-bold text-brand-blue bg-blue-50 px-2 py-1 rounded-full uppercase italic">{selectedPost.comments_count} Recorded</span>
                            </div>

                            <div className="flex-1 overflow-y-auto pr-2 space-y-4 custom-scrollbar bg-slate-50/30 p-2 rounded-2xl">
                                {selectedPost.comments && selectedPost.comments.length > 0 ? (
                                    selectedPost.comments.map((comment, index) => {
                                        const profileUrl = selectedPost.platform === 'instagram'
                                            ? `https://instagram.com/${comment.username && comment.username !== 'user' ? comment.username : ''}`
                                            : `https://facebook.com/${comment.id}`;

                                        // Robust Fallback: If no handle, go to the post itself to see the comment in context
                                        const finalUrl = (selectedPost.platform === 'instagram' && (!comment.username || comment.username === 'user' || profileUrl === 'https://instagram.com/'))
                                            ? selectedPost.permalink
                                            : profileUrl;

                                        return (
                                            <div key={index} className="flex gap-4 animate-in fade-in slide-in-from-right-4 duration-500" style={{ animationDelay: `${index * 50}ms` }}>
                                                <a href={finalUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 pt-1 group/avatar">
                                                    <div className="w-10 h-10 bg-white rounded-2xl flex items-center justify-center text-slate-300 border border-slate-200 group-hover/avatar:border-brand-blue group-hover/avatar:text-brand-blue transition-all overflow-hidden shadow-sm">
                                                        <User size={20} />
                                                    </div>
                                                </a>
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between gap-2 mb-1">
                                                        <a
                                                            href={finalUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-[11px] font-black text-brand-blue underline uppercase tracking-tight hover:text-blue-700 decoration-blue-200"
                                                        >
                                                            @{comment.name}
                                                        </a>
                                                        <span className="text-[9px] text-slate-400 font-bold uppercase italic">
                                                            {new Date(comment.timestamp).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    <div className="bg-white p-4 rounded-2xl rounded-tl-none border border-slate-100 relative shadow-sm">
                                                        <p className="text-xs text-slate-700 leading-relaxed font-semibold">
                                                            {comment.message}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="h-40 flex flex-col items-center justify-center text-center space-y-4 opacity-40">
                                        <MessageCircle size={40} className="text-slate-300" />
                                        <p className="font-black text-xs uppercase tracking-[0.2em]">Nothing to display</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};
